// Typed client for the sm4rt-xero-integration Azure Functions API.
// Calls go to sm4rt-api's staff-authenticated passthrough (/api/xero-int/* ->
// xero-integration /api/*), which adds the function key server-side so the browser
// never holds it. Dev: the CRA '/api' proxy sends this to sm4rt-api on :7071, which
// forwards to xero-integration on :7077. Only allow-listed routes are forwarded —
// a new endpoint here needs an entry in sm4rt-api's api/xero_integration_proxy too.

const API_BASE_URL = '/api/xero-int';

// ----- Shared envelope ---------------------------------------------------

export interface Pagination {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: Pagination;
}

// ----- Metrics -----------------------------------------------------------

export interface XeroMetrics {
  contacts_missing_xero_id: number;
  invoices_missing_xero_id: number;
  outstanding_invoices: number | null;
}

// ----- Missing-Xero-ID list rows ----------------------------------------

export interface ContactMissingXeroIdRow {
  guid: string;
  cr001_id: string | null;
  fullname: string | null;
  business_name: string | null;
  email: string | null;
  lookup_status: string | null;
}

export interface InvoiceMissingXeroIdRow {
  guid: string;
  cr001_number: number | null;
  date: string | null;
  total: number | null;
  lookup_status: string | null;
  customer: {
    cr001_id: string | null;
    fullname: string | null;
    business_name: string | null;
  };
}

// ----- Webhook health ----------------------------------------------------

export interface WebhookHealth {
  last_event_utc: string | null;
  last_processed_utc: string | null;
  queue_depth: number | null;
  errors?: string[];
}

// ----- Bulk-op request shapes -------------------------------------------

export interface SendMonthlyParams {
  max_age_days?: number;
  dry_run?: boolean;
}

export interface BackfillContactsParams {
  cr001_id?: number | string;
  cr001_ids?: Array<number | string>;
  all_missing?: boolean;
  limit?: number;
  retry_previously_skipped?: boolean;
}

export interface BackfillContactsByNameParams {
  cr001_id?: number | string;
  cr001_ids?: Array<number | string>;
  all_missing?: boolean;
  limit?: number;
  retry_previously_skipped?: boolean;
}

export interface BackfillInvoiceIdsParams {
  cr001_number?: string | number;
  cr001_numbers?: Array<string | number>;
  all_missing?: boolean;
  limit?: number;
  retry_previously_skipped?: boolean;
}

export interface BackfillResourceTypesParams {
  cr001_number?: string | number;
  cr001_numbers?: Array<string | number>;
  all_missing?: boolean;
  strategy?: 'probe_xero' | 'by_total_sign';
  limit?: number;
}

export interface MarkZeroInvoicesParams {
  cr001_number?: string | number;
  cr001_numbers?: Array<string | number>;
  all_missing?: boolean;
  limit?: number;
}

export interface RefreshModifiedParams {
  since?: string;
}

// ----- Generic helpers ---------------------------------------------------

function buildQuery(obj: Record<string, string | number | boolean | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== '') {
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`Request failed (${response.status}): ${text}`);
  }
  return response.json();
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, { method: 'GET', signal });
  return handleResponse<T>(response);
}

async function postJson<T>(
  path: string,
  body: unknown,
  signal?: AbortSignal
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  return handleResponse<T>(response);
}

// ----- Send-monthly response shape --------------------------------------

export interface WouldSendInvoice {
  invoice_id: string;
  invoice_number: string | null;
  contact_id: string | null;
  contact_name: string | null;
  amount_due: number | null;
  date: string | null;
}

export interface SendMonthlyFailure {
  invoice_id: string | null;
  error: string;
}

export interface SendMonthlyXeroInvoicesResponse {
  dry_run: boolean;
  max_age_days: number;
  customers_processed: number;
  customers_skipped_no_xero_id: Array<string | number>;
  invoices_found: number;
  sent: number;
  would_send: WouldSendInvoice[];
  failed: SendMonthlyFailure[];
}

// ----- Per-invoice op response shapes -----------------------------------

export interface SendInvoiceEmailResponse {
  invoice_number: string;
  xero_invoice_id: string;
  sent: true;
}

export interface MarkInvoiceSentResponse {
  invoice_number: string;
  xero_invoice_id: string;
  marked_sent: true;
}

export interface EnrichmentFields {
  cr001_xero_status: string | null;
  cr001_xero_total: number | null;
  cr001_xero_amount_due: number | null;
  cr001_xero_amount_paid: number | null;
  cr001_xero_fully_paid_on_date: string | null;
  cr001_xero_is_paid: boolean | null;
  cr001_xero_online_invoice_url: string | null;
  cr001_xero_history: string | null;
  cr001_xero_last_sent_datetime: string | null;
}

export interface EnrichInvoiceResponse {
  invoice_number: string;
  xero_invoice_id: string;
  dataverse_guid: string;
  enriched_fields: EnrichmentFields;
}

// ----- Read endpoints ----------------------------------------------------

export async function getXeroMetrics(
  options?: { include_previously_skipped?: boolean },
  signal?: AbortSignal
): Promise<XeroMetrics> {
  const qs = buildQuery({
    include_previously_skipped: options?.include_previously_skipped,
  });
  return getJson<XeroMetrics>(`/metrics${qs}`, signal);
}

export async function getContactsMissingXeroId(
  page: number,
  pageSize: number,
  options?: { include_previously_skipped?: boolean },
  signal?: AbortSignal
): Promise<PaginatedResponse<ContactMissingXeroIdRow>> {
  const qs = buildQuery({
    page,
    pageSize,
    include_previously_skipped: options?.include_previously_skipped,
  });
  return getJson<PaginatedResponse<ContactMissingXeroIdRow>>(
    `/contacts-missing-xero-id${qs}`,
    signal
  );
}

export async function getInvoicesMissingXeroId(
  page: number,
  pageSize: number,
  options?: { include_previously_skipped?: boolean },
  signal?: AbortSignal
): Promise<PaginatedResponse<InvoiceMissingXeroIdRow>> {
  const qs = buildQuery({
    page,
    pageSize,
    include_previously_skipped: options?.include_previously_skipped,
  });
  return getJson<PaginatedResponse<InvoiceMissingXeroIdRow>>(
    `/invoices-missing-xero-id${qs}`,
    signal
  );
}

export async function getXeroWebhookHealth(
  signal?: AbortSignal
): Promise<WebhookHealth> {
  return getJson<WebhookHealth>('/webhooks/xero/health', signal);
}

// ----- Bulk-op endpoints ------------------------------------------------

export async function runSendMonthlyXeroInvoices(
  params: SendMonthlyParams,
  signal?: AbortSignal
): Promise<SendMonthlyXeroInvoicesResponse> {
  return postJson<SendMonthlyXeroInvoicesResponse>(
    '/send-monthly-xero-invoices',
    params,
    signal
  );
}

export async function runBackfillXeroContactIds(
  params: BackfillContactsParams,
  signal?: AbortSignal
): Promise<unknown> {
  return postJson('/backfill-xero-contact-ids', params, signal);
}

export async function runBackfillXeroContactIdsByName(
  params: BackfillContactsByNameParams,
  signal?: AbortSignal
): Promise<unknown> {
  return postJson('/backfill-xero-contact-ids-by-name', params, signal);
}

export async function runBackfillXeroInvoiceIds(
  params: BackfillInvoiceIdsParams,
  signal?: AbortSignal
): Promise<unknown> {
  return postJson('/backfill-xero-invoice-ids', params, signal);
}

export async function runBackfillXeroInvoiceResourceTypes(
  params: BackfillResourceTypesParams,
  signal?: AbortSignal
): Promise<unknown> {
  return postJson('/backfill-xero-invoice-resource-types', params, signal);
}

export async function runMarkZeroInvoices(
  params: MarkZeroInvoicesParams,
  signal?: AbortSignal
): Promise<unknown> {
  return postJson('/mark-zero-invoices', params, signal);
}

export async function runRefreshXeroModified(
  params: RefreshModifiedParams,
  signal?: AbortSignal
): Promise<unknown> {
  return postJson('/refresh-xero-modified', params, signal);
}

// ----- Per-invoice ops --------------------------------------------------

export async function sendInvoiceEmail(
  invoiceNumber: string | number,
  signal?: AbortSignal
): Promise<SendInvoiceEmailResponse> {
  return postJson<SendInvoiceEmailResponse>(
    `/invoices/${encodeURIComponent(String(invoiceNumber))}/send-email`,
    undefined,
    signal
  );
}

export async function markInvoiceSent(
  invoiceNumber: string | number,
  signal?: AbortSignal
): Promise<MarkInvoiceSentResponse> {
  return postJson<MarkInvoiceSentResponse>(
    `/invoices/${encodeURIComponent(String(invoiceNumber))}/mark-sent`,
    undefined,
    signal
  );
}

export async function enrichInvoice(
  invoiceNumber: string | number,
  signal?: AbortSignal
): Promise<EnrichInvoiceResponse> {
  return postJson<EnrichInvoiceResponse>(
    `/invoices/${encodeURIComponent(String(invoiceNumber))}/enrich`,
    undefined,
    signal
  );
}
